import React, { useCallback, useId, useState } from "react";
import { Popover } from "@mui/material";
import { HexColorPicker } from "react-colorful";
import {
  STAGE_COLOR_PRESETS,
  SWIMLANE_TINT_PRESETS,
  buildTintFromHex,
  getSwimlaneColorSwatchStyles,
  getTintedSwimlaneChipStyles,
  isTintColorEqual,
  normalizeStageColor,
} from "./colorUtils";
import { stopPropagation } from "./nodeUiStyles";
import "./NodeColorPicker.css";

/**
 * Canonical color picker for the platform — preset swatches, optional hex panel,
 * and hex input. Supports solid hex colors (`mode="hex"`) and pastel tint pairs
 * (`mode="tint"`) used by kanban swimlanes.
 */
const NodeColorPicker = React.memo(function NodeColorPicker({
  label = "Color",
  value,
  onChange,
  /** `hex` for solid colors; `tint` for `{ background, border }` swimlane tints. */
  mode = "hex",
  presets,
  disabled = false,
  /** Square swatch trigger only — no label or value text. */
  swatchOnly = false,
  /** Compact framed trigger for inline chip/toolbar use (`swatchOnly` only). */
  embedded = false,
  /** When false, only preset swatches are shown (tint mode defaults to false). */
  allowCustom,
  onPickerClose,
}) {
  const labelId = useId();
  const [anchor, setAnchor] = useState(null);
  const isTintMode = mode === "tint";
  const resolvedPresets = presets ?? (isTintMode ? SWIMLANE_TINT_PRESETS : STAGE_COLOR_PRESETS);
  const showCustomPicker = allowCustom ?? true;

  const resolvedHex = isTintMode ? null : normalizeStageColor(value);
  const resolvedTint = isTintMode && value?.background ? value : null;
  const pickerColor =
    (isTintMode
      ? normalizeStageColor(resolvedTint?.border || resolvedTint?.background)
      : resolvedHex) || STAGE_COLOR_PRESETS[0];
  const ariaLabel = label || "Color";

  const tintedStyles = resolvedTint ? getTintedSwimlaneChipStyles(resolvedTint) : null;
  const swatchStyles = isTintMode
    ? getSwimlaneColorSwatchStyles(resolvedTint, tintedStyles)
    : null;

  const handleOpen = useCallback(
    (event) => {
      stopPropagation(event);
      if (!disabled) setAnchor(event.currentTarget);
    },
    [disabled]
  );

  const handleClose = useCallback(() => {
    setAnchor(null);
    onPickerClose?.();
  }, [onPickerClose]);

  const emitHex = useCallback(
    (next) => {
      onChange?.(normalizeStageColor(next));
    },
    [onChange]
  );

  const emitTint = useCallback(
    (next) => {
      onChange?.(next);
    },
    [onChange]
  );

  const handleHexPick = useCallback(
    (hex) => {
      if (isTintMode) {
        const tint = buildTintFromHex(hex);
        if (tint) emitTint(tint);
        return;
      }
      emitHex(hex);
    },
    [emitHex, emitTint, isTintMode]
  );

  const handleHexInput = useCallback(
    (event) => {
      const next = normalizeStageColor(event.target.value);
      if (!next) return;
      if (isTintMode) {
        const tint = buildTintFromHex(next);
        if (tint) emitTint(tint);
        return;
      }
      emitHex(next);
    },
    [emitHex, emitTint, isTintMode]
  );

  const handleReset = useCallback(() => {
    onChange?.(null);
    handleClose();
  }, [onChange, handleClose]);

  const isPresetActive = (preset) => {
    if (isTintMode) return isTintColorEqual(resolvedTint, preset);
    return resolvedHex === normalizeStageColor(preset);
  };

  const rootClassName = [
    "node-color-picker",
    swatchOnly ? "node-color-picker--swatch-only" : "",
    embedded ? "node-color-picker--embedded" : "",
    isTintMode ? "node-color-picker--tint" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClassName}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {!swatchOnly && label && (
        <span className="node-color-picker__label" id={labelId}>
          {label}
        </span>
      )}
      <button
        type="button"
        className={`node-color-picker__trigger nodrag${
          embedded ? " node-color-picker__trigger--embedded" : ""
        }`}
        onClick={handleOpen}
        disabled={disabled}
        aria-label={swatchOnly ? ariaLabel : undefined}
        aria-labelledby={!swatchOnly && label ? labelId : undefined}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        style={
          embedded && swatchStyles
            ? {
                backgroundColor: swatchStyles.frame,
                borderColor: swatchStyles.ring,
              }
            : undefined
        }
      >
        {isTintMode ? (
          <span
            className={`node-color-picker__swatch${
              swatchStyles ? "" : " node-color-picker__swatch--default"
            }${embedded ? " node-color-picker__swatch--embedded-fill" : ""}`}
            style={
              swatchStyles
                ? {
                    backgroundColor: swatchStyles.backgroundColor,
                    borderColor: swatchStyles.borderColor,
                  }
                : undefined
            }
          />
        ) : (
          <span
            className={`node-color-picker__swatch${
              resolvedHex ? "" : " node-color-picker__swatch--default"
            }`}
            style={resolvedHex ? { background: resolvedHex } : undefined}
          />
        )}
        {!swatchOnly && (
          <span className="node-color-picker__value">
            {isTintMode
              ? resolvedTint?.background || "Default"
              : resolvedHex || "Default"}
          </span>
        )}
      </button>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            className: "node-color-picker__panel",
            sx: {
              mt: 0.5,
              borderRadius: "8px",
              border: "1px solid var(--color-divider, #e2e8f0)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <div
          className={
            isTintMode
              ? "node-color-picker__tint-presets"
              : "node-color-picker__presets"
          }
        >
          {resolvedPresets.map((preset, index) => {
            const key = isTintMode ? preset.background : preset;
            return (
              <button
                key={key || index}
                type="button"
                className={`node-color-picker__preset${
                  isPresetActive(preset) ? " node-color-picker__preset--active" : ""
                }${isTintMode ? " node-color-picker__preset--tint" : ""}`}
                style={
                  isTintMode
                    ? {
                        backgroundColor: preset.background,
                        borderColor: preset.border,
                      }
                    : { background: preset }
                }
                onClick={() => {
                  if (isTintMode) emitTint(preset);
                  else emitHex(preset);
                }}
                aria-label={isTintMode ? `Use ${preset.background}` : `Use ${preset}`}
              />
            );
          })}
        </div>

        {showCustomPicker && (
          <>
            <div className="node-color-picker__picker">
              <HexColorPicker color={pickerColor} onChange={handleHexPick} />
            </div>

            <div className="node-color-picker__hex">
              <span className="node-color-picker__hex-label">Hex</span>
              <input
                className="node-color-picker__hex-input nodrag"
                value={
                  isTintMode
                    ? normalizeStageColor(resolvedTint?.border) || pickerColor
                    : resolvedHex || pickerColor
                }
                onChange={handleHexInput}
                spellCheck={false}
                aria-label="Hex color"
              />
            </div>
          </>
        )}

        {(resolvedHex || resolvedTint) && (
          <button type="button" className="node-color-picker__reset nodrag" onClick={handleReset}>
            Reset to default
          </button>
        )}
      </Popover>
    </div>
  );
});

export default NodeColorPicker;
